import { useTranslation } from 'react-i18next';

import BICYCLE_GROUPS from '../constants/bicycle';
import BOAT_GROUPS from '../constants/boat';
import CAR_GROUPS from '../constants/car';
import FAMILY_GROUPS from '../constants/family';
import LEGAL_GROUPS from '../constants/legal';
import MISC_GROUPS from '../constants/misc';
import RESIDENCE_GROUPS from '../constants/residence';
import TRAILER_GROUPS from '../constants/trailer';
import type {
  RiskObjectGroups as RiskObjectGroupsType,
  RiskObjectMinimal,
} from '../types';
import {
  isBicycle,
  isBoat,
  isCar,
  isFamily,
  isLegal,
  isMisc,
  isResidence,
  isTrailer,
} from '../utils';

import RiskObjectGroup from './RiskObjectGroup';
import RiskObjectProperty from './RiskObjectProperty';

type Props = {
  riskObject: RiskObjectMinimal;
};

function Group<T extends RiskObjectMinimal>({
  group,
  riskObject,
}: {
  group: RiskObjectGroupsType<T>[number];
  riskObject: T;
}) {
  const { t } = useTranslation();

  const isEmpty = !group.properties.some((prop) => {
    const value = prop.getValue(riskObject);

    return value !== undefined && value !== null;
  });

  if (isEmpty) return null;

  return (
    <RiskObjectGroup key={group.title} title={t(group.title)}>
      {group.properties.map((prop) => (
        <RiskObjectProperty
          key={prop.label}
          config={prop}
          riskObject={riskObject}
        />
      ))}
    </RiskObjectGroup>
  );
}

function Groups<T extends RiskObjectMinimal>({
  groups,
  riskObject,
}: {
  groups: RiskObjectGroupsType<T>;
  riskObject: T;
}) {
  return (
    <div className="flex w-full flex-col gap-2">
      {groups.map((group) => (
        <Group key={group.title} group={group} riskObject={riskObject} />
      ))}
    </div>
  );
}

export default function RiskObjectGroups({ riskObject }: Props) {
  if (isCar(riskObject))
    return <Groups groups={CAR_GROUPS} riskObject={riskObject} />;

  if (isResidence(riskObject))
    return <Groups groups={RESIDENCE_GROUPS} riskObject={riskObject} />;

  if (isBicycle(riskObject))
    return <Groups groups={BICYCLE_GROUPS} riskObject={riskObject} />;

  if (isBoat(riskObject))
    return <Groups groups={BOAT_GROUPS} riskObject={riskObject} />;

  if (isFamily(riskObject))
    return <Groups groups={FAMILY_GROUPS} riskObject={riskObject} />;

  if (isLegal(riskObject))
    return <Groups groups={LEGAL_GROUPS} riskObject={riskObject} />;

  if (isMisc(riskObject))
    return <Groups groups={MISC_GROUPS} riskObject={riskObject} />;

  if (isTrailer(riskObject))
    return <Groups groups={TRAILER_GROUPS} riskObject={riskObject} />;

  return null;
}
